// src/pages/registro/RegistroVeci.js
//
// Formulario de registro de veci contra el backend real.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button, Stack,
  Alert, Divider, Grid, IconButton, InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import Logo from '../../components/Logo';
import TelefonoInput from '../../components/TelefonoInput';
import SelectorMunicipio from '../../components/registro/SelectorMunicipio';
import CampoUbicacionGPS from '../../components/registro/CampoUbicacionGPS';
import AceptacionLegal from '../../components/registro/AceptacionLegal';
import { registro } from '../../api/registroApi';

const CODIGOS_LEGALES = ['TC_VECI', 'POLITICA_DATOS', 'AUTORIZACION_DATOS'];

export default function RegistroVeci() {
  const navigate = useNavigate();

  const [f, setF] = useState({
    nombre: '', apellido: '', apodo: '',
    telIndicativo: '57', telNumero: '', telefono: '', telefonoValido: false,
    correo: '',
    password: '', mostrarPass: false,
    direccion: '', detalleEntrega: '', barrio: '',
    municipioId: '',
    latitud: null, longitud: null,
  });

  const [aceptaciones, setAceptaciones] = useState({}); // { codigo: { aceptado, checksumAceptado } }
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [exito, setExito] = useState(false);

  const upd = (k) => (v) => setF(s => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const cambiarAceptacion = ({ codigo, aceptado, checksumAceptado }) => {
    setAceptaciones(s => ({ ...s, [codigo]: { aceptado, checksumAceptado } }));
  };

  const validar = () => {
    if (!f.nombre.trim())    return 'Escribe tu nombre.';
    if (!f.apellido.trim())  return 'Escribe tu apellido.';
    if (!f.telefonoValido)   return 'Teléfono inválido.';
    if (!f.correo.includes('@')) return 'Correo inválido.';
    if ((f.password || '').length < 6) return 'La contraseña debe tener al menos 6 caracteres.';
    if (!f.direccion.trim()) return 'Escribe tu dirección.';
    if (!f.municipioId)      return 'Selecciona tu municipio.';
    for (const c of CODIGOS_LEGALES) {
      if (!aceptaciones[c]?.aceptado) return 'Debes aceptar todos los documentos legales.';
    }
    return null;
  };

  const enviar = async () => {
    const err = validar();
    if (err) { setError(err); return; }
    setError('');
    setCargando(true);

    const payload = {
      nombre:         f.nombre.trim(),
      apellido:       f.apellido.trim(),
      telefono:       f.telefono,
      correo:         f.correo.trim(),
      password:       f.password,
      direccion:      f.direccion.trim(),
      detalleEntrega: f.detalleEntrega?.trim() || null,
      barrio:         f.barrio?.trim() || null,
      municipioID:    parseInt(f.municipioId, 10),
      apodo:          f.apodo?.trim() || null,
      latitud:        f.latitud,
      longitud:       f.longitud,
      aceptaciones: CODIGOS_LEGALES.map(c => ({
        codigo: c,
        checksumAceptado: aceptaciones[c].checksumAceptado,
      })),
    };

    try {
      await registro.registrarVeci(payload);
      setExito(true);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo completar el registro.');
    } finally { setCargando(false); }
  };

  if (exito) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#FBF7F0', display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="sm" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', py: 6 }}>
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 72, mb: 2 }} />
              <Typography variant="h4" sx={{ fontFamily: 'Fraunces', mb: 1 }}>¡Bienvenido, veci!</Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Tu cuenta fue creada. Revisa tu correo para confirmarlo. Ya puedes iniciar sesión.
              </Typography>
              <Button variant="contained" size="large" onClick={() => navigate('/login')}>
                Ir a iniciar sesión
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FBF7F0', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ py: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/registro')}><ArrowBackIcon /></IconButton>
        <Logo />
      </Container>

      <Container maxWidth="md" sx={{ flexGrow: 1, py: 3 }}>
        <Card>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="overline" color="text.secondary">Crear cuenta</Typography>
            <Typography variant="h4" sx={{ fontFamily: 'Fraunces', mb: 1 }}>Regístrate como veci</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Vas a poder ver las tiendas cercanas, pedir productos y pagarles como te acomode.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Stack spacing={3}>

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Datos personales</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Nombre(s)" value={f.nombre} onChange={upd('nombre')} required />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Apellido(s)" value={f.apellido} onChange={upd('apellido')} required />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Apodo (opcional)" value={f.apodo} onChange={upd('apodo')} />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Datos de contacto</Typography>
                <Stack spacing={2}>
                  <TelefonoInput
                    indicativo={f.telIndicativo}
                    numero={f.telNumero}
                    onChange={(ind, num, e164, valido) => setF(s => ({
                      ...s,
                      telIndicativo: ind, telNumero: num,
                      telefono: e164, telefonoValido: valido,
                    }))}
                  />
                  <TextField fullWidth label="Correo" type="email" value={f.correo} onChange={upd('correo')} required />
                  <TextField
                    fullWidth label="Contraseña"
                    type={f.mostrarPass ? 'text' : 'password'}
                    value={f.password}
                    onChange={upd('password')}
                    helperText="Mínimo 6 caracteres."
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setF(s => ({ ...s, mostrarPass: !s.mostrarPass }))} edge="end">
                            {f.mostrarPass ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>¿Dónde vives?</Typography>
                <Stack spacing={2}>
                  <SelectorMunicipio value={f.municipioId} onChange={upd('municipioId')} />
                  <TextField
                    fullWidth label="Dirección"
                    placeholder="Ej: Calle 45 #12-34"
                    value={f.direccion}
                    onChange={upd('direccion')}
                    required
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth label="Detalle (torre, apto, casa)"
                        placeholder="Ej: Torre 3, apto 502"
                        value={f.detalleEntrega}
                        onChange={upd('detalleEntrega')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth label="Barrio o conjunto (opcional)"
                        value={f.barrio}
                        onChange={upd('barrio')}
                      />
                    </Grid>
                  </Grid>
                  <CampoUbicacionGPS
                    lat={f.latitud} lng={f.longitud}
                    onChange={({ lat, lng }) => setF(s => ({ ...s, latitud: lat, longitud: lng }))}
                  />
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Términos legales</Typography>
                <Stack spacing={0.5}>
                  {CODIGOS_LEGALES.map(c => (
                    <AceptacionLegal
                      key={c}
                      codigo={c}
                      aceptado={!!aceptaciones[c]?.aceptado}
                      onChange={cambiarAceptacion}
                    />
                  ))}
                </Stack>
              </Box>

              <Button
                variant="contained" size="large" onClick={enviar} disabled={cargando}
                sx={{ py: 1.5, fontSize: 16 }}
              >
                {cargando ? 'Creando cuenta…' : 'Crear cuenta'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
